import * as React from "react"

import { cn } from "@/lib/utils"

type UserTextProps = React.ComponentProps<"bdi"> & {
  /** One line, cut with an ellipsis at the far end of the text's own direction. */
  truncate?: boolean
}

/**
 * Text a person typed or a directory supplied: a name, a subject, an email,
 * a campaign title (RTL-04).
 *
 * `<bdi dir="auto">` gives the value the direction of its own first letter
 * and isolates it from the sentence around it, so "محمد (HR)" in an English
 * row keeps its brackets and its neighbours keep their places. With
 * `unicode-bidi: plaintext` every line of a multi-line value decides its own
 * direction.
 *
 * `truncate` makes it a one-line block in the text's own direction, so an
 * Arabic value keeps its first words (a left-to-right block cuts the start of
 * Arabic text). The cost: a short Arabic value in an English table sits at
 * the right of its cell. In Chromium `text-align: match-parent` did not undo
 * that (measured 2026-09-26), so it is not attempted.
 *
 * Pass `dir="ltr"` for values that always read left to right: phone numbers,
 * email addresses, domains, IP addresses.
 */
function UserText({ truncate = false, dir = "auto", className, ...props }: UserTextProps) {
  return (
    <bdi
      data-slot="user-text"
      dir={dir}
      className={cn(
        // Only for dir="auto": plaintext would let the content override an explicit direction.
        dir === "auto" && "[unicode-bidi:plaintext]",
        truncate && "block min-w-0 truncate",
        className
      )}
      {...props}
    />
  )
}

export { UserText }
